import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Badge from '@src/components/Badge';
import { ORDER_STATE_CONFIG, OrderStatusInfo } from '../../utils/order-status';

interface Props {
  className?: string;
  title: string;
  statusInfo: OrderStatusInfo;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  noBorder?: boolean;
}

function OrderBadge({
  statusInfo,
}: {
  statusInfo: OrderStatusInfo;
}): JSX.Element | null {
  const { t } = useTranslation(['translation']);
  if (!statusInfo.badge) {
    return null;
  }
  switch (statusInfo.badge) {
    case 'DRAFT':
      return (
        <Badge color="gray">{t('dashboard.order_badge.draft', 'Draft')}</Badge>
      );
    case 'PENDING':
      return (
        <Badge color="yellow">
          {t('dashboard.order_badge.pending', 'In progress')}
        </Badge>
      );
    case 'CANCELLATION_PENDING':
      return (
        <Badge color="yellow">
          {t(
            'dashboard.order_badge.cancellation_pending',
            'Cancellation pending',
          )}
        </Badge>
      );
    case 'ACTION_REQUIRED':
      return (
        <Badge color="red">
          {t('dashboard.order_badge.action_required', 'Action required')}
        </Badge>
      );
    case 'COMPLETE':
      return (
        <Badge color="green">
          {t('dashboard.order_badge.complete', 'Complete')}
        </Badge>
      );
    case 'CANCELLED':
      return (
        <Badge color="gray">
          {t('dashboard.order_badge.cancelled', 'Cancelled')}
        </Badge>
      );
    case 'INACTIVE':
      return (
        <Badge color="gray">
          {t('dashboard.order_badge.inactive', 'Inactive')}
        </Badge>
      );
    case 'UNPAID':
      return (
        <Badge color="red">{t('dashboard.order_badge.unpaid', 'Unpaid')}</Badge>
      );
    default:
      throw new Error(`Unknown badge status ${statusInfo.badge as string}`);
  }
}

export function OrderItem({
  className,
  statusInfo,
  actions,
  title,
  children,
  noBorder,
}: Props): JSX.Element {
  const { t } = useTranslation('translation');

  // a little cheeky workaround to get the parser not to throw warnings :)
  // https://github.com/i18next/i18next-parser/issues/657
  const manualTranslate = t;

  const statusInfoWithDefaults = {
    ...ORDER_STATE_CONFIG[statusInfo.state],
    ...statusInfo,
  };

  return (
    <li
      className={clsx(
        'flex flex-col justify-between space-y-4 overflow-hidden bg-white px-6 py-4 md:flex-row md:items-end md:space-x-4 md:space-y-0',
        noBorder ? '' : 'rounded-md shadow',
        className,
      )}
    >
      <div className="space-y-4">
        {statusInfoWithDefaults.badge && (
          <div>
            <OrderBadge statusInfo={statusInfoWithDefaults} />
          </div>
        )}
        <h3>{title}</h3>
        {children}
        {statusInfo.message && (
          <div className="flex items-center space-x-2">
            <span className="text-sm">
              {manualTranslate(statusInfo.message)}
            </span>
          </div>
        )}
      </div>
      {actions}
    </li>
  );
}
